/**
 * In-App Purchase Service
 * Unified payment handling for iOS (StoreKit), Android (Google Play Billing), and Web (Razorpay)
 */

import { Capacitor } from '@capacitor/core';
import API_URL from '../config/apiConfig';
import { rlog } from '../utils/railwayLogger';

// Platform-specific Product IDs (MUST match App Store Connect & Google Play Console)
const IOS_PRODUCT_IDS = {
  STARTER_199: 'com.taraanaya.starter',
  PREMIUM_599: 'com.taraanaya.premium',
  ULTIMATE_999: 'com.taraanaya.ultimate',
};

const ANDROID_PRODUCT_IDS = {
  STARTER_199: 'taraanaya_199',
  PREMIUM_599: 'taraanaya_599',
  ULTIMATE_999: 'taraanaya_999',
};

// Credit product IDs per platform
const IOS_CREDIT_IDS = {
  CREDITS_500: 'com.taraanaya.credits_500',
  CREDITS_1000: 'com.taraanaya.credits_1000',
  CREDITS_1500: 'com.taraanaya.credits_1500',
};

const ANDROID_CREDIT_IDS = {
  CREDITS_500: 'taraanaya_credits_500',
  CREDITS_1000: 'taraanaya_credits_1000',
  CREDITS_1500: 'taraanaya_credits_1500',
};

export const getCreditIdsForPlatform = () => {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' ? IOS_CREDIT_IDS : ANDROID_CREDIT_IDS;
};

// Get product IDs based on current platform
export const getProductIdsForPlatform = () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return IOS_PRODUCT_IDS;
  }
  return ANDROID_PRODUCT_IDS; // Android and Web use same IDs
};

// Export for backward compatibility
export const PRODUCT_IDS = getProductIdsForPlatform();

// Product details for UI (keyed by product type, not ID)
// NOTE: `price` fields retained for internal tracking only. Real price shown to
// user comes from Apple/Google Store (native) or Razorpay (web) at checkout time
// to prevent label-vs-charge mismatch.
export const PRODUCT_DETAILS_BY_TYPE = {
  STARTER_199: {
    name: 'Starter',
    price: 199,
    credits: 300,
    description: 'Life Insights + 3 Sage Questions',
    type: 'subscription',
    unlocks: ['life_insights', 'sage_questions'],
  },
  PREMIUM_599: {
    name: 'Premium',
    price: 599,
    credits: 300,
    description: 'Life Insights + 3 Questions + Future Reels',
    type: 'subscription',
    unlocks: ['life_insights', 'sage_questions', 'future_reels'],
  },
  ULTIMATE_999: {
    name: 'Ultimate',
    price: 999,
    credits: 300,
    description: 'Everything + Full Timeline Access',
    type: 'subscription',
    unlocks: ['life_insights', 'sage_questions', 'future_reels', 'timeline'],
  },
};

// Build PRODUCT_DETAILS keyed by product ID for current platform
const buildProductDetails = () => {
  const ids = getProductIdsForPlatform();
  const details = {};
  Object.keys(PRODUCT_DETAILS_BY_TYPE).forEach(key => {
    details[ids[key]] = PRODUCT_DETAILS_BY_TYPE[key];
  });
  return details;
};

export const PRODUCT_DETAILS = buildProductDetails();

// Check current platform
export const getPlatform = () => {
  const platform = Capacitor.getPlatform();
  return platform; // 'ios', 'android', or 'web'
};

export const isNative = () => {
  return Capacitor.isNativePlatform();
};

// Store instance
let store = null;
let isInitialized = false;

/**
 * Resolve the current user_id at call time (from localStorage).
 * Works across app restarts — purchase flow uses this for applicationUsername
 * so backend can link the purchase to the correct account even if user
 * closes app mid-purchase and reopens later.
 */
const resolveUserId = () => {
  return (
    localStorage.getItem('user_id') ||
    localStorage.getItem('session_id') ||
    localStorage.getItem('taraanaya_device_id') ||
    'unknown'
  );
};

/**
 * GLOBAL approved-purchase handler.
 * Registered once during initializeStore() — BEFORE store.initialize() —
 * so it also catches auto-fired `approved` events from:
 *   • restorePurchases()
 *   • re-launches where a pending purchase exists in Google Play / Apple
 *   • subscription renewals
 * This is the reliable path that guarantees /api/iap/verify is called.
 */
const globalApprovedHandler = async (transaction) => {
  try {
    const platform = getPlatform();
    const productId = transaction.products?.[0]?.id || transaction.productId;
    if (!productId) {
      console.warn('[IAP] approved: no productId, skipping');
      try { transaction.finish(); } catch {}
      return;
    }
    // Filter out the bogus "app receipt" event Apple fires on launch.
    // It has product=<bundle_id> (e.g. com.taraanaya.app) and txn=appstore.application.
    // It is NOT an actual purchase — just app-level entitlement metadata. Ignoring.
    const txnId = transaction.transactionId || transaction.id || '';
    const isBogusAppReceipt = (
      txnId === 'appstore.application' ||
      productId === 'com.taraanaya.app' ||
      // Defensive: any product id that doesn't start with our prefix or isn't credit_*
      (!productId.startsWith('com.taraanaya.starter') &&
       !productId.startsWith('com.taraanaya.premium') &&
       !productId.startsWith('com.taraanaya.ultimate') &&
       !productId.startsWith('com.taraanaya.credits_') &&
       !productId.startsWith('taraanaya_'))
    );
    if (isBogusAppReceipt) {
      console.log(`[IAP] ⏭️  Ignoring bogus app receipt event (productId=${productId}, txn=${txnId})`);
      try { transaction.finish(); } catch {}
      return;
    }
    const userId = resolveUserId();
    const receipt = platform === 'ios'
      ? (transaction.appStoreReceipt || transaction.transactionId)
      : (transaction.purchaseToken || transaction.transactionId);
    
    console.log(`[IAP] 🔔 approved fired | product=${productId} user=${userId} txn=${txnId}`);
    rlog('iap_approved_fired', {
      product_id: productId,
      user_id: userId,
      platform,
      transaction_id: transaction.transactionId || transaction.id || '',
      has_receipt: !!receipt,
      receipt_len: (receipt || '').length,
      txn_type: transaction.type,
      transaction_state: transaction.state,
      prev_plan: localStorage.getItem('plan_id'),
      prev_credits: localStorage.getItem('sage_credits'),
    });
    
    const verifyRes = await fetch(`${API_URL}/api/iap/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform,
        user_id: userId,
        product_id: productId,
        transaction_id: transaction.transactionId || transaction.id || '',
        receipt: receipt || ''
      })
    });
    const verifyData = await verifyRes.json().catch(() => ({}));
    try { transaction.finish(); } catch (fe) { console.warn('[IAP] finish() error:', fe); }
    rlog('iap_verify_response', {
      product_id: productId,
      user_id: userId,
      http_status: verifyRes.status,
      success: !!verifyData.success,
      already_processed: !!verifyData.already_processed,
      credits_added: verifyData.credits_added,
      unlocks_count: (verifyData.unlocks || []).length,
      message: verifyData.message,
    });
    
    if (verifyData.success) {
      const product = PRODUCT_DETAILS[productId] || {};
      const credits = verifyData.credits_added || product.credits || 300;
      const unlocks = verifyData.unlocks || product.unlocks || [];
      const expiry = verifyData.subscription_expires || new Date(Date.now() + 30*24*60*60*1000).toISOString();
      
      const prevPlan = localStorage.getItem('plan_id');
      const prevCredits = parseInt(localStorage.getItem('sage_credits') || '0', 10) || 0;
      
      // Update localStorage for immediate UI reaction
      if (product.type === 'subscription' || unlocks.length) {
        localStorage.setItem('is_premium', 'true');
        localStorage.setItem('isPremium', 'true');
        localStorage.setItem('plan_unlocks', JSON.stringify(unlocks));
        localStorage.setItem('plan_id', productId);
        localStorage.setItem('premium_expires', expiry);
      }
      // ── CREDITS RECONCILIATION ──
      // 1. already_processed (duplicate replay): server returns authoritative
      //    `credits_balance`. Use it as-is — DO NOT overwrite to baseline,
      //    otherwise spent credits (e.g. 300→200 after using 100) reset to 300
      //    on every app reopen (cordova-plugin-purchase replays approved txns).
      // 2. Fresh consumable purchase: ADD credits to existing balance.
      // 3. Fresh subscription: server already incremented credits via $inc, but
      //    we cache the new total for offline UX. Trust server's `credits_balance`
      //    if provided, else fall back to (prev + credits).
      if (verifyData.already_processed && typeof verifyData.credits_balance === 'number') {
        localStorage.setItem('sage_credits', String(verifyData.credits_balance));
      } else if (product.type === 'consumable') {
        const prev = parseInt(localStorage.getItem('sage_credits') || '0', 10) || 0;
        localStorage.setItem('sage_credits', String(prev + credits));
      } else {
        // Fresh subscription purchase — preserve any existing balance
        // (in case user had unused consumable credits before subscribing)
        const prev = parseInt(localStorage.getItem('sage_credits') || '0', 10) || 0;
        const nextBalance = typeof verifyData.credits_balance === 'number'
          ? verifyData.credits_balance
          : prev + credits;
        localStorage.setItem('sage_credits', String(nextBalance));
      }
      rlog('iap_entitlements_applied', {
        product_id: productId,
        product_type: product.type,
        prev_plan: prevPlan,
        new_plan: productId,
        prev_credits: prevCredits,
        new_credits: parseInt(localStorage.getItem('sage_credits') || '0', 10),
        unlocks_count: unlocks.length,
        is_upgrade: !!prevPlan && prevPlan !== productId,
      });
      try { window.dispatchEvent(new Event('credits-updated')); } catch {}
      try { window.dispatchEvent(new CustomEvent('iap-result', { detail: {
        success: true, product_id: productId, credits_added: credits, unlocks, message: 'Purchase successful!'
      }})); } catch {}
      // Clear in-flight flag — verify succeeded.
      try { localStorage.removeItem('iap_purchase_in_flight'); } catch {}
      // If this purchase unlocks future_reels and content isn't already
      // generated, kick off background generation IMMEDIATELY so by the time
      // user navigates to FutureStoryExact, content is ready (or close to).
      // Without this pre-warm, generation only triggers on page mount → user
      // perceives 30-60s delay watching an empty/loading screen.
      try {
        if (
          product.type === 'subscription' &&
          unlocks.includes('future_reels') &&
          !verifyData.already_processed
        ) {
          const existingStory = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
          const hasReels = existingStory?.future_story?.domain_reels &&
            Object.keys(existingStory.future_story.domain_reels).length > 0;
          if (!hasReels) {
            console.log('[IAP] 🚀 Pre-warming future_reels generation in background');
            rlog('future_reels_prewarm_start', { user_id: userId, product_id: productId });
            localStorage.setItem('future_reels_generating', 'true');
            localStorage.setItem('future_reels_gen_start', String(Date.now()));
            // Don't await — fire-and-forget so the IAP handler returns quickly
            generatePremiumContent(['future_reels']).then((res) => {
              rlog('future_reels_prewarm_done', {
                user_id: userId,
                success: !!res?.success,
                ms: Date.now() - parseInt(localStorage.getItem('future_reels_gen_start') || '0'),
              });
            }).catch((err) => {
              rlog('future_reels_prewarm_error', { error: err?.message || String(err) });
            });
          }
        }
      } catch (prewarmErr) {
        console.warn('[IAP] pre-warm failed:', prewarmErr);
      }
    } else {
      console.warn('[IAP] verify failed:', verifyData);
      rlog('iap_verify_failed', {
        product_id: productId,
        user_id: userId,
        message: verifyData.message || verifyData.detail,
      });
      try { localStorage.removeItem('iap_purchase_in_flight'); } catch {}
      try { window.dispatchEvent(new CustomEvent('iap-result', { detail: {
        success: false, message: verifyData.message || verifyData.detail || 'Verification failed'
      }})); } catch {}
    }
  } catch (err) {
    console.error('[IAP] globalApprovedHandler error:', err);
    rlog('iap_approved_handler_error', { error: err?.message || String(err) });
    try { transaction.finish(); } catch {}
    try { localStorage.removeItem('iap_purchase_in_flight'); } catch {}
    try { window.dispatchEvent(new CustomEvent('iap-result', { detail: {
      success: false, message: 'Verification network error'
    }})); } catch {}
  }
};

/**
 * Initialize the IAP store
 */
export const initializeStore = async () => {
  if (isInitialized) return true;
  
  const platform = getPlatform();
  console.log(`🛒 Initializing IAP for platform: ${platform}`);
  
  if (platform === 'web') {
    // Web uses Razorpay - no store initialization needed
    isInitialized = true;
    return true;
  }
  
  try {
    // For iOS/Android, use cordova-plugin-purchase
    if (window.CdvPurchase) {
      store = window.CdvPurchase.store;
      
      // Set validator URL (backend fallback path called by plugin automatically)
      store.validator = `${API_URL}/api/iap/validate`;
      
      // Set applicationUsername function — plugin attaches this to order()
      // as obfuscatedExternalAccountId (Android) / applicationUsername (iOS).
      // Backend reads this from /api/iap/validate so the purchase is linked
      // to the correct user even via plugin's auto-validator flow.
      try { store.applicationUsername = () => resolveUserId(); } catch {}
      
      // Register products — subscriptions + consumable credits.
      // `group: 'taraanaya_premium'` on all 3 subscription plans enables
      // Google Play's AUTOMATIC replacement flow: when user orders a new plan
      // while another from the same group is active, Play Store auto-cancels
      // the old one and charges prorated price — even without us supplying
      // oldPurchaseToken manually.
      const creditIds = getCreditIdsForPlatform();
      const products = [
        { id: PRODUCT_IDS.STARTER_199, type: window.CdvPurchase.ProductType.PAID_SUBSCRIPTION, group: 'taraanaya_premium' },
        { id: PRODUCT_IDS.PREMIUM_599, type: window.CdvPurchase.ProductType.PAID_SUBSCRIPTION, group: 'taraanaya_premium' },
        { id: PRODUCT_IDS.ULTIMATE_999, type: window.CdvPurchase.ProductType.PAID_SUBSCRIPTION, group: 'taraanaya_premium' },
        { id: creditIds.CREDITS_500, type: window.CdvPurchase.ProductType.CONSUMABLE },
        { id: creditIds.CREDITS_1000, type: window.CdvPurchase.ProductType.CONSUMABLE },
        { id: creditIds.CREDITS_1500, type: window.CdvPurchase.ProductType.CONSUMABLE },
      ];
      
      const platformId = platform === 'ios' 
        ? window.CdvPurchase.Platform.APPLE_APPSTORE 
        : window.CdvPurchase.Platform.GOOGLE_PLAY;
      
      store.register(products.map(p => ({ ...p, platform: platformId })));
      
      // CRITICAL: Register GLOBAL approved handler BEFORE initialize()
      // so auto-fired approved events (restored purchases, pending txns,
      // renewals) also hit the verify endpoint.
      try {
        store.when().approved((txn) => { globalApprovedHandler(txn); });
      } catch (handlerErr) {
        console.warn('[IAP] Failed to register global approved handler:', handlerErr);
      }
      
      // Initialize the store
      await store.initialize([platformId]);
      
      isInitialized = true;
      console.log('✅ IAP Store initialized successfully');

      // ====== DIAGNOSTIC: Log which products Apple/Google returned ======
      // After init, Apple/Google asynchronously hydrate product details.
      // If product is missing here → App Store Connect / Play Console setup issue
      // (most common: paid agreement not signed, product not "Ready to Submit",
      //  banking incomplete, or product ID typo).
      try {
        // Wait briefly for async product hydration
        await new Promise((r) => setTimeout(r, 1500));
        const allIds = [
          PRODUCT_IDS.STARTER_199,
          PRODUCT_IDS.PREMIUM_599,
          PRODUCT_IDS.ULTIMATE_999,
          creditIds.CREDITS_500,
          creditIds.CREDITS_1000,
          creditIds.CREDITS_1500,
        ];
        const productStatus = allIds.map(id => {
          const p = store.get(id);
          return {
            id,
            found: !!p,
            canPurchase: p?.canPurchase || false,
            price: p?.pricing?.price || null,
            state: p?.state || 'unknown',
          };
        });
        const foundCount = productStatus.filter(p => p.found).length;
        console.log(`🛒 IAP Products hydration: ${foundCount}/${allIds.length} recognized by ${platform === 'ios' ? 'App Store' : 'Play Store'}`);
        console.log('🛒 Product details:', JSON.stringify(productStatus, null, 2));
        rlog('iap_products_hydrated', {
          platform,
          total: allIds.length,
          found: foundCount,
          products: productStatus,
        });
        if (foundCount === 0) {
          console.error('❌ NO PRODUCTS FOUND IN STORE — Most likely causes:');
          console.error('   1. App Store Connect: Paid Apps Agreement not active');
          console.error('   2. App Store Connect: Products not in "Ready to Submit" state');
          console.error('   3. Banking/Tax info missing');
          console.error('   4. Product IDs do not match exactly (case-sensitive)');
          console.error('   5. Sandbox account not signed in (Settings → App Store → Sandbox Account)');
        }
      } catch (diagErr) {
        console.warn('IAP diagnostic logging failed:', diagErr);
      }

      return true;
    } else {
      console.warn('⚠️ CdvPurchase not available - running in web mode');
      isInitialized = true;
      return true;
    }
  } catch (error) {
    console.error('❌ IAP initialization error:', error);
    return false;
  }
};

/**
 * Refresh native store prices.
 *
 * cordova-plugin-purchase caches the localized price per product when the store
 * first hydrates. If the user changes their App Store / Play Store country, or
 * if the developer changes the price tier in App Store Connect / Play Console
 * (e.g. switching base currency from USD to INR), the *cached* price in the JS
 * layer goes stale — but Apple's purchase sheet shows the *new* live price.
 *
 * Result: app shows "$1.99" while Apple actually charges "₹249/month" — a
 * confusing/deceptive UX that App Review will reject. Calling store.update()
 * forces a re-fetch so cached prices match what Apple will charge.
 *
 * Wired up below to fire on app foreground (Capacitor `appStateChange`).
 */
export const refreshStorePrices = async () => {
  if (!store) return false;
  try {
    await store.update();
    return true;
  } catch (e) {
    console.warn('[IAP] refreshStorePrices failed:', e);
    return false;
  }
};

/**
 * Get product information from the store
 */
export const getProduct = (productId) => {
  if (!store) {
    return PRODUCT_DETAILS[productId] || null;
  }
  
  try {
    const product = store.get(productId);
    if (product) {
      return {
        ...PRODUCT_DETAILS[productId],
        storePrice: product.pricing?.price || null,
        canPurchase: product.canPurchase,
      };
    }
  } catch (e) {
    console.warn('Error getting product:', e);
  }
  
  return PRODUCT_DETAILS[productId] || null;
};

/**
 * Get the localized formatted price string for a product from the native store.
 * Returns something like "₹199.00", "$1.99", "€7.99" depending on user's
 * App Store / Play Store country. Returns null on web or if store not ready.
 */
export const getStorePriceFormatted = (productId) => {
  if (!store || !window.CdvPurchase) return null;
  try {
    const product = store.get(productId);
    // cordova-plugin-purchase v13: pricing.price is the localized string
    return product?.pricing?.price || null;
  } catch (_) {
    return null;
  }
};

/**
 * Purchase a product - Razorpay popup opens INSIDE the app
 * Capacitor hostname = taraanaya.ai (whitelisted in Razorpay)
 * Web on taraanaya.ai = also whitelisted
 * Only dev/preview environments fall back to mock
 */
/**
 * Check if current domain is whitelisted for Razorpay payments.
 * Razorpay LIVE mode blocks payments from unregistered domains.
 * Only taraanaya.ai (and Capacitor apps with hostname=taraanaya.ai) are whitelisted.
 */
export const isPaymentDomainAllowed = () => {
  const hostname = window.location.hostname || '';
  if (hostname.includes('taraanaya.ai')) return true;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  return false;
};

/**
 * Create Razorpay Payment Link and open it.
 * For NATIVE: Opens in Chrome Custom Tab (not WebView — avoids CapacitorHttp interception)
 * For WEB: Redirects in current window
 */
const redirectToPaymentLink = async (packageId, userId) => {
  const userName = localStorage.getItem('user_name') || '';
  const userEmail = localStorage.getItem('user_email') || '';
  const userPhone = localStorage.getItem('user_phone') || '';
  const originUrl = window.location.origin;
  const isDemoDevice = localStorage.getItem('is_test_device') === 'true';

  const response = await fetch(`${API_URL}/api/razorpay/create-payment-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      package_id: packageId,
      user_id: userId,
      name: userName,
      email: userEmail,
      phone: userPhone,
      origin_url: originUrl,
      demo_mode: isDemoDevice,
    }),
  });

  const data = await response.json();
  if (!data.success || !data.payment_url) {
    throw new Error(data.detail || 'Failed to create payment link');
  }

  const isNativePlatform = Capacitor.isNativePlatform();
  
  if (isNativePlatform) {
    // NATIVE: Open in Chrome Custom Tab / SFSafariViewController
    // This is a REAL browser — CapacitorHttp does NOT affect it
    try {
      const { Browser } = await import('@capacitor/browser');
      
      // Store payment info for verification when user returns
      localStorage.setItem('pending_payment_link_id', data.payment_link_id || '');
      localStorage.setItem('pending_payment_package', packageId);
      localStorage.setItem('pending_payment_time', Date.now().toString());
      
      // Listen for browser close to check payment status
      Browser.addListener('browserFinished', () => {
        console.log('[Payment] Browser closed — checking payment status...');
        window.dispatchEvent(new Event('payment-browser-closed'));
      });
      
      // Don't await — open() might hang when app goes to background
      Browser.open({ 
        url: data.payment_url,
        toolbarColor: '#1a1a2e'
      });
      
      return { success: false, redirected: true, browserOpened: true };
    } catch (browserError) {
      console.error('[Payment] Browser plugin failed:', browserError);
      // Fallback: open in system browser via window.open
      window.open(data.payment_url, '_system');
      return { success: false, redirected: true, browserOpened: true };
    }
  } else {
    // WEB: Redirect in current window
    window.location.href = data.payment_url;
    return { success: false, redirected: true };
  }
};

/**
 * Purchase a product — Backend-mode-aware.
 * Backend PAYMENT_MODE controls everything:
 *   "mock" → auto-succeeds (for testing)
 *   "production" → opens real Razorpay SDK popup
 * NO client-side fallback in production — payment failure = real failure.
 */
/**
 * Normalize any plan_id (taraanaya_*, premium_*, com.taraanaya.*) to a
 * canonical tier key: 'starter' | 'premium' | 'ultimate' | '' (unknown).
 * Used everywhere for upgrade-level comparisons to eliminate plan_id
 * race conditions (backend returns premium_599, IAP writes taraanaya_599).
 */
export const normalizePlanId = (pid) => {
  if (!pid || typeof pid !== 'string') return '';
  const NORMALIZE = {
    'starter': 'starter', 'starter_199': 'starter',
    'taraanaya_199': 'starter', 'taraanaya-starter-199': 'starter',
    'com.taraanaya.starter': 'starter', 'com.taraanaya.starter_199': 'starter',
    'premium': 'premium', 'premium_599': 'premium',
    'taraanaya_599': 'premium', 'taraanaya-premium-599': 'premium',
    'com.taraanaya.premium': 'premium', 'com.taraanaya.premium_599': 'premium',
    'ultimate': 'ultimate', 'ultimate_999': 'ultimate',
    'taraanaya_999': 'ultimate', 'taraanaya-ultimate-999': 'ultimate',
    'com.taraanaya.ultimate': 'ultimate', 'com.taraanaya.ultimate_999': 'ultimate',
  };
  if (NORMALIZE[pid]) return NORMALIZE[pid];
  // Heuristic fallback
  const lower = pid.toLowerCase();
  if (lower.includes('starter') || lower.includes('199')) return 'starter';
  if (lower.includes('premium') || lower.includes('599')) return 'premium';
  if (lower.includes('ultimate') || lower.includes('999')) return 'ultimate';
  return '';
};

// Single-flight guard: prevents double-tap on purchase buttons from opening
// two concurrent Google Play sheets (which loses proration context on the
// 2nd sheet and charges full price).
const _purchaseInFlight = { value: false };

export const purchaseProduct = async (productId, userId) => {
  const platform = getPlatform();
  console.log(`Purchase requested: ${productId} for user ${userId} on ${platform}`);
  
  // Guard against double-tap
  if (_purchaseInFlight.value) {
    rlog('purchase_blocked_in_flight', { product_id: productId });
    return { success: false, message: 'A purchase is already in progress. Please wait.' };
  }
  _purchaseInFlight.value = true;
  // Persistent flag survives app backgrounding (e.g. user switches to UPI app
  // mid-payment). On app resume we read this flag to show a "Verifying your
  // purchase…" overlay until /verify completes — fixes the UX where back-nav
  // from Google Play billing instantly bounces to dashboard with stale credits.
  try {
    localStorage.setItem('iap_purchase_in_flight', JSON.stringify({
      product_id: productId,
      started_at: Date.now(),
      platform,
    }));
  } catch {}
  
  try {
  rlog('purchase_requested', {
    product_id: productId,
    user_id: userId,
    platform,
    current_plan: localStorage.getItem('plan_id'),
    current_plan_normalized: normalizePlanId(localStorage.getItem('plan_id') || ''),
    current_credits: localStorage.getItem('sage_credits'),
    is_premium: localStorage.getItem('is_premium'),
  });
  
  // Map frontend product IDs to backend package IDs
  const packageMap = {
    'starter': 'starter', 'taraanaya_starter': 'starter', 'taraanaya_starter_monthly': 'starter',
    'premium': 'premium', 'taraanaya_premium': 'premium', 'taraanaya_premium_monthly': 'premium',
    'ultimate': 'ultimate', 'taraanaya_ultimate': 'ultimate', 'taraanaya_ultimate_monthly': 'ultimate',
    // Android/Web IDs
    'taraanaya-starter-199': 'starter',
    'taraanaya-premium-599': 'premium',
    'taraanaya-ultimate-999': 'ultimate',
    // iOS IDs (App Store Connect)
    'com.taraanaya.starter_199': 'starter',
    'com.taraanaya.premium_599': 'premium',
    'com.taraanaya.ultimate_999': 'ultimate',
    // Current App Store Connect IDs
    'com.taraanaya.starter': 'starter',
    'com.taraanaya.premium': 'premium',
    'com.taraanaya.ultimate': 'ultimate',
    // Google Play subscription IDs
    'taraanaya_199': 'starter',
    'taraanaya_599': 'premium',
    'taraanaya_999': 'ultimate',
  };
  const backendPackageId = packageMap[productId] || productId;
  
  // Find product details for local reference
  let product = PRODUCT_DETAILS[productId];
  if (!product) {
    const typeKey = Object.keys(PRODUCT_DETAILS_BY_TYPE).find(k => 
      PRODUCT_IDS[k] === productId || 
      IOS_PRODUCT_IDS[k] === productId || 
      ANDROID_PRODUCT_IDS[k] === productId
    );
    if (typeKey) product = PRODUCT_DETAILS_BY_TYPE[typeKey];
  }
  if (!product) {
    product = { name: 'Starter', price: 199, credits: 300, type: 'subscription', unlocks: ['life_insights', 'sage_questions'] };
  }
  
  // ===== NATIVE PLATFORMS: Use StoreKit (iOS) / Google Play Billing (Android) =====
  // Per Apple / Google store policies, digital goods on native MUST go through
  // their respective billing systems. Never route Android/iOS through Razorpay.
  
  if (platform === 'ios' || platform === 'android') {
    // Idempotent init — safe if already initialized (checks isInitialized internal flag)
    try {
      if (!isInitialized) {
        rlog('iap_purchase_lazy_init_start', { product_id: productId });
        const ok = await initializeStore();
        rlog('iap_purchase_lazy_init_done', { product_id: productId, ok });
      }
    } catch (e) {
      rlog('iap_purchase_lazy_init_error', { product_id: productId, error: e?.message });
    }
    // Wait-for-ready: cordova-plugin-purchase initializes async after app boot.
    // If user taps purchase within first 2-3s, store may not be ready. Retry
    // with short backoff up to 5s instead of failing immediately.
    if (!store || !window.CdvPurchase) {
      rlog('iap_store_not_ready_retry', { product_id: productId, has_window: !!window.CdvPurchase, has_store: !!store });
      const readyDeadline = Date.now() + 5000;
      while ((!store || !window.CdvPurchase) && Date.now() < readyDeadline) {
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!store || !window.CdvPurchase) {
        rlog('iap_store_ready_timeout', { product_id: productId, waited_ms: 5000 });
        return {
          success: false,
          message: `${platform === 'ios' ? 'App Store' : 'Google Play'} billing is still loading. Please wait a moment and try again.`,
        };
      }
      rlog('iap_store_ready_after_wait', { product_id: productId, waited_ms: Date.now() - (readyDeadline - 5000) });
    }
    
    // Use native IAP via cordova-plugin-purchase
    console.log(`🛒 Native IAP purchase: ${productId} on ${platform}`);
    
    try {
      // Refresh owned purchases so the plugin knows about the current
      // subscription — required for the proration/replacement flow to find
      // the old purchase token. Plugin v13 hydrates the `owned`/`receipt`
      // state asynchronously via the `approved` callback *after* restore
      // returns, so we wait a short additional grace period for that to
      // settle before reading any local receipts.
      const restoreStart = Date.now();
      try { await store.restorePurchases(); } catch (e) {
        rlog('iap_restore_error', { error: e?.message || String(e) });
      }
      const restoreMs = Date.now() - restoreStart;
      try { await new Promise((r) => setTimeout(r, 1500)); } catch {}
      rlog('iap_restore_complete', {
        restore_ms: restoreMs,
        local_receipts: store.localReceipts?.length || 0,
        verified_receipts: store.verifiedReceipts?.length || 0,
      });
      
      // Find the product in the store
      const storeProduct = store.get(productId);
      if (!storeProduct) {
        console.warn(`Product ${productId} not found in store, trying refresh...`);
        await store.update();
      }
      
      const offer = storeProduct?.getOffer?.();
      if (!offer && !storeProduct) {
        console.error(`Product ${productId} still not found after refresh`);
        // Fall through to Razorpay as backup
      } else {
        // UPGRADE DETECTION: if user has an active subscription on a different
        // product, Google Play charges a prorated price instead of the full
        // price. We pass the old purchase so Play Billing knows to REPLACE.
        const currentPlanId = localStorage.getItem('plan_id') || '';
        const TIER_LEVEL = { 'starter': 1, 'premium': 2, 'ultimate': 3 };
        const oldTier = normalizePlanId(currentPlanId);
        const newTier = normalizePlanId(productId);
        const oldLevel = TIER_LEVEL[oldTier] || 0;
        const newLevel = TIER_LEVEL[newTier] || 0;
        const isUpgrade = oldLevel > 0 && newLevel > oldLevel;
        rlog('upgrade_detection', {
          current_plan_raw: currentPlanId,
          current_tier: oldTier,
          target_product: productId,
          target_tier: newTier,
          old_level: oldLevel,
          new_level: newLevel,
          is_upgrade: isUpgrade,
        });
        
        // Build order options
        const orderOpts = { applicationUsername: userId };
        
        if (isUpgrade && platform === 'android') {
          // Find the currently-owned old subscription product in the store
          try {
            // Map normalized tier → actual Google Play product registered in the store.
            // Using `oldTier` (already normalized above) eliminates plan_id alias
            // confusion (taraanaya_599 vs premium_599 vs com.taraanaya.premium).
            const TIER_TO_PLAY_ID = {
              'starter': PRODUCT_IDS.STARTER_199,
              'premium': PRODUCT_IDS.PREMIUM_599,
              'ultimate': PRODUCT_IDS.ULTIMATE_999,
            };
            const oldPlayProductId = TIER_TO_PLAY_ID[oldTier];
            const oldProduct = oldPlayProductId ? store.get(oldPlayProductId) : null;
            
            // Aggressive token resolution — try EVERY known path in cordova-plugin-purchase v13.
            // In the field we observed `product.receipt` being empty `{}`, so we ALSO walk:
            //   store.localReceipts[].transactions[].purchaseToken
            //   store.localReceipts[].transactions[].products[].productId match
            let oldPurchaseToken = null;
            let tokenSource = 'none';
            
            // Path 1: product.receipt.purchaseToken
            if (oldProduct?.receipt?.purchaseToken) {
              oldPurchaseToken = oldProduct.receipt.purchaseToken;
              tokenSource = 'product.receipt';
            }
            // Path 2: product.transaction chain
            else if (oldProduct?.transaction?.receipt?.purchaseToken) {
              oldPurchaseToken = oldProduct.transaction.receipt.purchaseToken;
              tokenSource = 'product.transaction.receipt';
            }
            else if (oldProduct?.transaction?.purchaseToken) {
              oldPurchaseToken = oldProduct.transaction.purchaseToken;
              tokenSource = 'product.transaction';
            }
            // Path 3: product.transactions[] array
            else if (oldProduct?.transactions?.length) {
              for (const t of oldProduct.transactions) {
                if (t?.receipt?.purchaseToken) { oldPurchaseToken = t.receipt.purchaseToken; tokenSource = 'product.transactions[].receipt'; break; }
                if (t?.purchaseToken) { oldPurchaseToken = t.purchaseToken; tokenSource = 'product.transactions[]'; break; }
              }
            }
            // Path 4: store.localReceipts — the plugin's master list of owned receipts
            if (!oldPurchaseToken && store.localReceipts?.length) {
              for (const r of store.localReceipts) {
                const txns = r?.transactions || [];
                for (const t of txns) {
                  const hasOurProduct = (t?.products || []).some(p => p?.id === oldPlayProductId);
                  if (hasOurProduct) {
                    oldPurchaseToken = t.purchaseToken || t.nativePurchase?.purchaseToken || null;
                    if (oldPurchaseToken) { tokenSource = 'store.localReceipts[].transactions[]'; break; }
                  }
                }
                if (oldPurchaseToken) break;
              }
            }
            // Path 5: store.verifiedReceipts (post-server-verify)
            if (!oldPurchaseToken && store.verifiedReceipts?.length) {
              for (const r of store.verifiedReceipts) {
                for (const t of (r?.transactions || [])) {
                  if ((t?.products || []).some(p => p?.id === oldPlayProductId)) {
                    oldPurchaseToken = t.purchaseToken || null;
                    if (oldPurchaseToken) { tokenSource = 'store.verifiedReceipts[].transactions[]'; break; }
                  }
                }
                if (oldPurchaseToken) break;
              }
            }
            
            // Path 6 (BACKEND FALLBACK): when plugin state is empty (common after
            // app reinstall, data wipe, or when product.receipt={} as observed in
            // field logs), fetch the original purchaseToken from the backend. The
            // backend stored it during the initial /api/iap/verify call.
            if (!oldPurchaseToken && userId) {
              try {
                const beStart = Date.now();
                const resp = await fetch(`${API_URL}/api/iap/old-purchase-token/${encodeURIComponent(userId)}`, {
                  method: 'GET',
                  headers: { 'Content-Type': 'application/json' },
                });
                const beMs = Date.now() - beStart;
                if (resp.ok) {
                  const data = await resp.json();
                  rlog('iap_backend_token_fetch', {
                    user_id: userId,
                    http_status: resp.status,
                    fetch_ms: beMs,
                    success: !!data?.success,
                    source: data?.source,
                    product_id: data?.product_id,
                    token_len: (data?.purchase_token || '').length,
                  });
                  if (data?.success && data?.purchase_token) {
                    oldPurchaseToken = data.purchase_token;
                    tokenSource = `backend:${data.source || 'unknown'}`;
                    console.log('[IAP] 🌐 Retrieved old purchase token from backend:', {
                      source: data.source,
                      product: data.product_id,
                      tokenLen: oldPurchaseToken.length,
                    });
                  }
                } else {
                  rlog('iap_backend_token_http_error', { http_status: resp.status });
                }
              } catch (beErr) {
                console.warn('[IAP] Backend token fetch failed:', beErr?.message);
                rlog('iap_backend_token_exception', { error: beErr?.message });
              }
            }
            
            // Emit backend log with FULL introspection so operator can see
            // exactly where the token lives (or doesn't).
            try {
              fetch(`${API_URL}/api/iap/log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  event: 'upgrade_attempt',
                  user_id: userId,
                  from_plan: currentPlanId,
                  to_product: productId,
                  old_play_product_id: oldPlayProductId,
                  has_old_product: !!oldProduct,
                  has_old_purchase_token: !!oldPurchaseToken,
                  token_source: tokenSource,
                  old_product_owned: oldProduct?.owned,
                  old_product_state: oldProduct?.state,
                  old_receipt_keys: oldProduct?.receipt ? Object.keys(oldProduct.receipt) : [],
                  old_product_transactions_count: oldProduct?.transactions?.length || 0,
                  store_local_receipts_count: store.localReceipts?.length || 0,
                  store_verified_receipts_count: store.verifiedReceipts?.length || 0,
                  platform,
                })
              }).catch(() => {});
            } catch {}
            
            if (oldPurchaseToken) {
              const ProrationMode = window.CdvPurchase?.GooglePlay?.ProrationMode || {};
              orderOpts.googlePlay = {
                oldPurchaseToken,
                prorationMode: ProrationMode.IMMEDIATE_AND_CHARGE_PRORATED_PRICE
                  || 'IMMEDIATE_AND_CHARGE_PRORATED_PRICE',
              };
              console.log('[IAP] 🔼 Upgrade w/ proration → Google Play will charge only the difference:', {
                oldProduct: oldPlayProductId,
                newProduct: productId,
                tokenLen: oldPurchaseToken.length,
                tokenSource,
                hasOldProductInPlugin: !!oldProduct,
                prorationMode: orderOpts.googlePlay.prorationMode,
              });
            } else {
              console.warn('[IAP] Upgrade detected but old purchase token not resolvable — Google Play will charge FULL PRICE', {
                oldPlayProductId,
                hasOldProduct: !!oldProduct,
                hasToken: !!oldPurchaseToken,
                oldReceiptKeys: oldProduct?.receipt ? Object.keys(oldProduct.receipt) : [],
                oldProductState: oldProduct?.state,
              });
            }
          } catch (upErr) {
            console.warn('[IAP] Upgrade handler error (falling back to full price):', upErr);
          }
        }
        
        // Place the order — error means cancelled/failed
        let orderResult = null;
        const orderStart = Date.now();
        try {
          orderResult = offer ? await offer.order(orderOpts) : await store.order(storeProduct, orderOpts);
        } catch (orderErr) {
          console.warn('[IAP] order() threw:', orderErr);
          orderResult = orderErr;
        }
        const orderMs = Date.now() - orderStart;
        rlog('iap_order_returned', {
          product_id: productId,
          order_ms: orderMs,
          has_result: !!orderResult,
          result_code: orderResult?.code,
          result_message: (orderResult?.message || '').slice(0, 120),
          order_opts_has_proration: !!orderOpts.googlePlay?.oldPurchaseToken,
          order_opts_token_len: orderOpts.googlePlay?.oldPurchaseToken?.length || 0,
          proration_mode: orderOpts.googlePlay?.prorationMode,
        });
        
        if (orderResult) {
          const isCancelled = orderResult.code === 6777006 || (orderResult.message || '').toLowerCase().includes('cancel');
          rlog('iap_order_failed', {
            product_id: productId,
            cancelled: isCancelled,
            code: orderResult.code,
            message: (orderResult.message || '').slice(0, 200),
          });
          return { success: false, cancelled: isCancelled, message: isCancelled ? 'Purchase cancelled' : (orderResult.message || 'Purchase failed') };
        }
        
        // Wait for global approved handler to finish verification and fire iap-result
        return new Promise((resolve) => {
          const handler = (e) => { window.removeEventListener('iap-result', handler); resolve(e.detail); };
          window.addEventListener('iap-result', handler);
          setTimeout(() => {
            window.removeEventListener('iap-result', handler);
            rlog('iap_purchase_timeout', { product_id: productId });
            resolve({ success: false, message: 'Purchase timed out — will auto-restore on next app open' });
          }, 90000);
        });
      }
    } catch (nativeError) {
      console.error('Native IAP error:', nativeError);
      // Android/iOS: DO NOT fall back to Razorpay — per platform store policy,
      // digital goods must go through Google Play / App Store. Fail explicitly.
      return {
        success: false,
        message: nativeError?.message || `Unable to complete ${platform === 'ios' ? 'App Store' : 'Google Play'} purchase. Please retry.`,
      };
    }
    // If we reach here, store wasn't initialized — also don't fall through
    return {
      success: false,
      message: `${platform === 'ios' ? 'App Store' : 'Google Play'} is not ready. Please restart the app and try again.`,
    };
  }
  
  // ===== WEB / FALLBACK: Use Razorpay =====
  console.log('Using Razorpay payment flow...');
  const isDemoDevice = localStorage.getItem('is_test_device') === 'true';
  const orderRes = await fetch(`${API_URL}/api/razorpay/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      package_id: backendPackageId,
      user_id: userId,
      email: localStorage.getItem('user_email') || '',
      name: localStorage.getItem('user_name') || 'User',
      phone: localStorage.getItem('user_phone') || '',
      demo_mode: isDemoDevice
    })
  });
  const { parseJsonSafe: _safeJson } = await import('../utils/safeJson');
  const orderParsed = await _safeJson(orderRes);
  if (!orderParsed.ok || !orderParsed.data) {
    return { success: false, message: orderParsed.error || 'Could not reach payment server. Please retry.' };
  }
  const orderData = orderParsed.data;
  
  if (!orderData.success) {
    return { success: false, message: orderData.detail || orderData.message || 'Failed to create order' };
  }
  
  // Route based on backend payment mode
  if (orderData.mock) {
    // ===== MOCK MODE: Auto-verify (testing) =====
    console.log('Mock payment mode — auto-verifying...');
    const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        razorpay_order_id: orderData.order_id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
        razorpay_signature: 'mock',
        demo_mode: isDemoDevice
      })
    });
    const verifyParsed = await _safeJson(verifyRes);
    const verifyData = verifyParsed.data || {};
    
    if (!verifyParsed.ok || !verifyData.success) {
      return { success: false, message: verifyData.detail || verifyParsed.error || 'Payment verification failed. Please retry.' };
    }
    
    const mergedUnlocks = verifyData.unlocks || product.unlocks || [];
    const expiryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    
    _storeSubscriptionLocally(backendPackageId, mergedUnlocks, product.credits || 300, expiryDate);
    
    return {
      success: true,
      mock: true,
      message: 'Purchase successful!',
      product_id: productId,
      transaction_id: orderData.order_id,
      payment_id: `pay_mock_${Date.now()}`,
      credits_added: verifyData.sage_credits_added || product.credits || 300,
      unlocks: mergedUnlocks,
      expires_at: verifyData.expires_at || expiryDate.toISOString()
    };
  } else {
    // ===== PRODUCTION MODE: Razorpay SDK popup (All platforms) =====
    console.log('Production payment — opening Razorpay SDK popup...');
    
    if (!window.Razorpay || typeof window.Razorpay !== 'function') {
      return { success: false, message: 'Payment gateway not loaded. Please refresh and try again.' };
    }
    
    // Open Razorpay checkout popup
    return new Promise((resolve) => {
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency || 'INR',
        name: 'Taraanaya',
        description: `${orderData.package?.name || product.name} - Vedic Astrology`,
        image: '/sage-final.png',
        order_id: orderData.order_id,
        handler: async (response) => {
          // Retry verify-payment up to 3 times (handles flaky network / WebView CORS issues)
          const attemptVerify = async (attempt = 1) => {
            try {
              const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  user_id: userId
                })
              });
              const verifyParsed = await _safeJson(verifyRes);
              const verifyData = verifyParsed.data || {};
              
              if (verifyParsed.ok && verifyData.success) {
                const unlocks = verifyData.unlocks || orderData.package?.unlocks || product.unlocks || [];
                const credits = orderData.package?.sage_credits || product.credits || 300;
                const expiryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                
                _storeSubscriptionLocally(backendPackageId, unlocks, credits, expiryDate);
                
                resolve({
                  success: true,
                  payment_id: response.razorpay_payment_id,
                  transaction_id: response.razorpay_order_id,
                  unlocks,
                  credits_added: credits,
                  expires_at: verifyData.expires_at || expiryDate.toISOString(),
                  ...verifyData
                });
                return true;
              }
              // Retry on non-ok response too
              if (attempt < 3) {
                console.warn(`[iap] verify attempt ${attempt} failed, retrying in ${attempt*2}s`);
                await new Promise(r => setTimeout(r, attempt * 2000));
                return attemptVerify(attempt + 1);
              }
              resolve({ success: false, message: verifyData.message || verifyParsed.error || 'Payment verification failed. Contact support with your payment ID.' });
              return false;
            } catch (error) {
              if (attempt < 3) {
                console.warn(`[iap] verify attempt ${attempt} network error, retrying`, error?.message);
                await new Promise(r => setTimeout(r, attempt * 2000));
                return attemptVerify(attempt + 1);
              }
              const { sanitizeErrorMessage } = await import('../utils/safeJson');
              resolve({ success: false, message: sanitizeErrorMessage(error?.message, 'Payment verification error. Your payment has been captured — please restart the app and go to Profile to trigger auto-recovery.') });
              return false;
            }
          };
          await attemptVerify(1);
        },
        prefill: {
          name: orderData.prefill?.name || localStorage.getItem('user_name') || '',
          email: orderData.prefill?.email || localStorage.getItem('user_email') || '',
          contact: orderData.prefill?.contact || localStorage.getItem('user_phone') || '',
        },
        modal: {
          ondismiss: () => {
            resolve({ success: false, cancelled: true, message: 'Payment cancelled' });
          },
        },
        theme: { color: '#edc161' },
      };
      
      try {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          resolve({ success: false, message: resp.error?.description || 'Payment failed' });
        });
        rzp.open();
      } catch (err) {
        resolve({ success: false, message: 'Failed to open payment gateway: ' + err.message });
      }
    });
  }
  } finally {
    _purchaseInFlight.value = false;
  }
};

/**
 * Helper: Store subscription data locally after successful payment
 */
const _storeSubscriptionLocally = (planId, unlocks, credits, expiryDate) => {
  localStorage.setItem('is_premium', 'true');
  localStorage.setItem('isPremium', 'true');
  localStorage.setItem('premium_expires', expiryDate.toISOString());
  localStorage.setItem('plan_unlocks', JSON.stringify(unlocks));
  localStorage.setItem('plan_id', planId);
  localStorage.setItem('sage_credits', String(credits));
  window.dispatchEvent(new Event('credits-updated'));
};

/**
 * Verify purchase with backend
 */
const verifyPurchase = async (transaction, userId) => {
  // API_URL imported from apiConfig (has Railway fallback for Android)
  const platform = getPlatform();
  
  const response = await fetch(`${API_URL}/api/iap/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      platform,
      user_id: userId,
      product_id: transaction.products[0]?.id,
      transaction_id: transaction.transactionId,
      receipt: transaction.receipt || transaction.purchaseToken,
    }),
  });
  
  const { parseJsonSafe } = await import('../utils/safeJson');
  const parsed = await parseJsonSafe(response);
  if (!parsed.ok || !parsed.data) {
    throw new Error(parsed.error || 'Verification failed');
  }
  const result = parsed.data;
  
  if (!result.success) {
    throw new Error(result.message || 'Verification failed');
  }
  
  return result;
};

/**
 * Purchase with Razorpay (Web + Android) — delegates to unified purchaseProduct
 * Kept for backward compatibility with components that import it directly
 */
export const purchaseWithRazorpay = async (productId, userId) => {
  return purchaseProduct(productId, userId);
};

/**
 * Restore purchases (iOS/Android).
 * Combines native restorePurchases() + backend reconcile to claim any
 * orphan transactions (user_id="unknown") that were created before
 * login completed.
 */
export const restorePurchases = async (userId) => {
  const platform = getPlatform();
  const targetUserId = userId || resolveUserId();
  
  // 1) Backend reconcile — claims orphan iap_transactions & user_subscriptions
  //    where user_id="unknown" but applicationUsername was set. This catches
  //    the "user paid before login completed" edge case.
  let reconcileResult = null;
  try {
    const email = localStorage.getItem('user_email') || '';
    const phone = localStorage.getItem('user_phone') || '';
    const deviceId = localStorage.getItem('taraanaya_device_id') || '';
    const sessionId = localStorage.getItem('session_id') || '';
    const res = await fetch(`${API_URL}/api/iap/reconcile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: targetUserId,
        email,
        phone,
        device_id: deviceId,
        session_id: sessionId,
      }),
    });
    reconcileResult = await res.json().catch(() => null);
    if (reconcileResult?.success) {
      console.log('[IAP] reconcile:', reconcileResult);
    }
  } catch (e) {
    console.warn('[IAP] reconcile failed:', e?.message);
  }
  
  // 2) For web, nothing else to do
  if (platform === 'web' || !store) {
    return { success: true, reconcile: reconcileResult };
  }
  
  // 3) Native: trigger restorePurchases to re-fire `approved` for active subs
  //    Global approved handler (registered in init) will call /api/iap/verify.
  try {
    await store.restorePurchases();
  } catch (e) {
    console.warn('[IAP] store.restorePurchases failed:', e?.message);
  }
  
  return { success: true, reconcile: reconcileResult };
};

/**
 * Check if user has active subscription
 */
export const checkSubscriptionStatus = async (userId) => {
  // API_URL imported from apiConfig (has Railway fallback for Android)
  
  try {
    const response = await fetch(`${API_URL}/api/users/${userId}/subscription`);
    const data = await response.json();
    
    return {
      isActive: data.status === 'active',
      expiresAt: data.expires_at,
      planId: data.plan_id,
    };
  } catch (error) {
    console.error('Error checking subscription:', error);
    return { isActive: false };
  }
};

/**
 * Check if a specific feature is unlocked based on current plan
 * Features: 'life_insights', 'sage_questions', 'future_reels', 'timeline'
 * Uses localStorage as CACHE, but validates against backend on critical checks
 */
export const isFeatureUnlocked = (feature) => {
  const isPremium = localStorage.getItem('is_premium') === 'true' || localStorage.getItem('isPremium') === 'true';
  if (!isPremium) return false;
  
  const expires = localStorage.getItem('premium_expires');
  if (expires && new Date(expires) < new Date()) {
    // Expired — clear local cache
    localStorage.removeItem('is_premium');
    localStorage.removeItem('isPremium');
    localStorage.removeItem('plan_unlocks');
    return false;
  }
  
  const unlocks = JSON.parse(localStorage.getItem('plan_unlocks') || '[]');
  return unlocks.includes(feature);
};

/**
 * Server-side premium verification — call this before gating expensive operations
 * (e.g., before generating Future Reels, Timeline, etc.)
 * Updates localStorage cache from backend truth
 */
export const verifyPremiumFromServer = async () => {
  const userId = localStorage.getItem('user_id');
  if (!userId) return { is_premium: false, plan_unlocks: [] };
  
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${API_URL}/api/auth/verify-premium/${userId}`);
    if (!res.ok) return { is_premium: false, plan_unlocks: [] };
    
    const data = await res.json();
    
    // Update localStorage cache from server truth
    if (data.is_premium) {
      localStorage.setItem('is_premium', 'true');
      localStorage.setItem('isPremium', 'true');
      if (data.plan_unlocks) localStorage.setItem('plan_unlocks', JSON.stringify(data.plan_unlocks));
      if (data.sage_credits !== undefined) {
        localStorage.setItem('sage_credits', String(data.sage_credits));
        try { window.dispatchEvent(new Event('credits-updated')); } catch {}
      }
      if (data.expires_at) localStorage.setItem('premium_expires', data.expires_at);
      if (data.plan_id) localStorage.setItem('plan_id', data.plan_id);
    } else {
      localStorage.removeItem('is_premium');
      localStorage.removeItem('isPremium');
      localStorage.removeItem('plan_unlocks');
      localStorage.removeItem('sage_credits');
      localStorage.removeItem('premium_expires');
      try { window.dispatchEvent(new Event('credits-updated')); } catch {}
    }
    
    return data;
  } catch (e) {
    console.warn('Premium verification failed, using cached:', e.message);
    // Fallback to localStorage if network fails
    return {
      is_premium: localStorage.getItem('is_premium') === 'true',
      plan_unlocks: JSON.parse(localStorage.getItem('plan_unlocks') || '[]')
    };
  }
};

/**
 * Get current plan's unlocked features
 */
export const getUnlockedFeatures = () => {
  const isPremium = localStorage.getItem('is_premium') === 'true' || localStorage.getItem('isPremium') === 'true';
  if (!isPremium) return [];
  
  const expires = localStorage.getItem('premium_expires');
  if (expires && new Date(expires) < new Date()) return [];
  
  return JSON.parse(localStorage.getItem('plan_unlocks') || '[]');
};

/**
 * Get remaining sage credits
 */
export const getSageCredits = () => {
  return parseInt(localStorage.getItem('sage_credits') || '0', 10);
};

/**
 * Use sage credits (100 per question)
 */
export const useSageCredits = (amount = 100) => {
  const current = getSageCredits();
  if (current < amount) return false;
  localStorage.setItem('sage_credits', String(current - amount));
  try { window.dispatchEvent(new Event('credits-updated')); } catch {}
  return true;
};

/**
 * Trigger on-demand premium content generation after payment.
 * Generates future reels, life insights, timelines based on plan unlocks.
 * @param {string[]} unlocks - Features unlocked by the plan
 * @returns {Promise<object>} Generated premium content
 */
export const generatePremiumContent = async (unlocks = []) => {
  // API_URL imported from apiConfig (has Railway fallback for Android)
  
  // Get cache key from stored unified story data
  const storedData = localStorage.getItem('unified_story_data');
  let cacheKey = localStorage.getItem('story_cache_key') || '';
  
  // Get birth data
  const birthRaw = localStorage.getItem('birth_details') || localStorage.getItem('birthDetails');
  if (!birthRaw) {
    console.warn('No birth details found for premium generation');
    return { success: false, error: 'No birth details' };
  }
  
  let birthData;
  try { birthData = JSON.parse(birthRaw); } catch(e) { return { success: false, error: 'Invalid birth data' }; }
  
  // Try to extract cache_key from unified story data if not stored separately
  if (!cacheKey && storedData) {
    try {
      const ud = JSON.parse(storedData);
      cacheKey = ud.cache_key || '';
    } catch(e) {}
  }
  
  // Generate cache key if still missing
  if (!cacheKey) {
    const year = parseInt(birthData.year);
    const month = parseInt(birthData.month);
    const day = parseInt(birthData.day);
    const hour = parseInt(birthData.hour) || 12;
    const minute = parseInt(birthData.minute) || 0;
    const lat = parseFloat(birthData.latitude) || 28.61;
    const lon = parseFloat(birthData.longitude) || 77.21;
    const keyStr = `${birthData.name || ''}_${year}_${month}_${day}_${hour}_${minute}_${lat.toFixed(2)}_${lon.toFixed(2)}`;
    // Simple hash - same as backend
    let hash = 0;
    for (let i = 0; i < keyStr.length; i++) {
      const char = keyStr.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0;
    }
    cacheKey = Math.abs(hash).toString(16);
  }
  
  const payload = {
    cache_key: cacheKey,
    unlocks: unlocks,
    user_id: localStorage.getItem('user_id') || localStorage.getItem('session_id') || localStorage.getItem('taraanaya_device_id') || '',
    name: birthData.name || 'User',
    year: parseInt(birthData.year) || 2000,
    month: parseInt(birthData.month) || 1,
    day: parseInt(birthData.day) || 1,
    hour: parseInt(birthData.hour) || 12,
    minute: parseInt(birthData.minute) || 0,
    latitude: parseFloat(birthData.latitude) || 28.6139,
    longitude: parseFloat(birthData.longitude) || 77.209,
    gender: birthData.gender || 'unknown',
    place: birthData.place || ''
  };
  
  console.log(`🔓 Generating premium content: ${unlocks.join(', ')}`);
  rlog('premium_content_start', {
    unlocks,
    cache_key: cacheKey,
    user_id: payload.user_id,
    content_version: (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}').future_reels; } catch { return null; } })(),
  });
  const genStart = Date.now();
  
  try {
    // Step 1: Start generation (returns immediately)
    const response = await fetch(`${API_URL}/api/unified-story/generate-premium`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    const startResult = await response.json();
    
    if (!startResult.success) {
      rlog('premium_content_start_failed', { message: startResult.message, http_status: response.status });
      return startResult;
    }
    
    // Step 2: Poll for completion (background generation on server)
    // Use accelerated polling for first 30s (1.5s interval) then fall back to
    // 3s — gives users much faster visible feedback when content arrives.
    const MAX_POLL_TIME = 300000; // 5 minutes max
    const FAST_POLL_INTERVAL = 1500;
    const SLOW_POLL_INTERVAL = 3000;
    const FAST_POLL_DURATION = 30000; // first 30s use fast polling
    const startTime = Date.now();
    let pollCount = 0;
    
    while (Date.now() - startTime < MAX_POLL_TIME) {
      const elapsed = Date.now() - startTime;
      const interval = elapsed < FAST_POLL_DURATION ? FAST_POLL_INTERVAL : SLOW_POLL_INTERVAL;
      await new Promise(r => setTimeout(r, interval));
      pollCount += 1;
      
      try {
        const statusRes = await fetch(`${API_URL}/api/unified-story/premium-status/${cacheKey}`);
        const statusData = await statusRes.json();
        
        if (statusData.status === 'complete') {
          console.log('✅ Premium generation complete!');
          rlog('premium_content_complete', {
            cache_key: cacheKey,
            elapsed_ms: Date.now() - genStart,
            poll_count: pollCount,
            has_future_reels: !!statusData.generated?.future_reels,
            has_life_insights: !!statusData.generated?.life_insights,
            algorithm_version: statusData.generated?.algorithm_version || statusData.algorithm_version,
          });
          const generated = statusData.generated || {};
          
          // Update unified_story_data with new future reels
          if (generated.future_reels && !generated.future_reels.error) {
            try {
              const existing = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
              if (!existing.future_story) existing.future_story = {};
              existing.future_story.domain_reels = generated.future_reels;
              localStorage.setItem('unified_story_data', JSON.stringify(existing));
              console.log('✅ Future reels stored in unified_story_data');
              localStorage.removeItem('future_reels_generating');
              // CRITICAL: Reset TTS flag so audio re-generates for new future reels
              localStorage.removeItem('tts_generation_triggered');
              window.dispatchEvent(new Event('future-reels-ready'));
            } catch(e) {
              console.warn('Failed to update unified_story_data:', e);
            }
          }
          
          return { success: true, generated };
        } else if (statusData.status === 'failed') {
          console.error('Premium generation failed on server:', statusData.error);
          return { success: false, error: statusData.error || 'Generation failed' };
        }
        // Still generating — continue polling
        console.log(`⏳ Premium content generating... ${statusData.elapsed_seconds || '?'}s elapsed`);
      } catch (pollErr) {
        console.warn('Poll error (will retry):', pollErr);
      }
    }
    
    // Timeout
    return { success: false, error: 'Generation timed out after 3 minutes' };
  } catch (error) {
    console.error('Premium generation failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Purchase credit refill — Backend-mode-aware.
 *   Mock mode: create-order + auto-verify (instant success)
 *   Production mode: Payment Link redirect (works from any domain)
 */
export const purchaseCreditRefill = async (creditPackId, userId) => {
  const platform = getPlatform();
  
  try {
    // NATIVE (Android/iOS): Use native IAP (Google Play / Apple StoreKit)
    // Per platform store policies, native digital goods MUST use native billing.
    if (platform === 'ios' || platform === 'android') {
      // CRITICAL: initializeStore() is idempotent — safe to call every time.
      // Without this, Credits Refill modal (opened directly from Dashboard)
      // hits a never-initialized store and fails with "still loading" after 5s.
      // The subscription flow works because PaymentPlans.jsx already calls
      // initializeStore() on mount.
      try {
        if (!isInitialized) {
          rlog('iap_credit_lazy_init_start', { credit_pack_id: creditPackId });
          const ok = await initializeStore();
          rlog('iap_credit_lazy_init_done', { credit_pack_id: creditPackId, ok });
        }
      } catch (e) {
        rlog('iap_credit_lazy_init_error', { credit_pack_id: creditPackId, error: e?.message });
      }
      // Wait-for-ready (plugin may init async after app boot)
      if (!store || !window.CdvPurchase) {
        rlog('iap_credit_store_not_ready_retry', { credit_pack_id: creditPackId });
        const readyDeadline = Date.now() + 5000;
        while ((!store || !window.CdvPurchase) && Date.now() < readyDeadline) {
          await new Promise((r) => setTimeout(r, 300));
        }
        if (!store || !window.CdvPurchase) {
          rlog('iap_credit_store_ready_timeout', { credit_pack_id: creditPackId });
          return {
            success: false,
            message: `${platform === 'ios' ? 'App Store' : 'Google Play'} billing is still loading. Please wait a moment and try again.`,
          };
        }
      }
      // Map the generic web ID (credits_500 / credits_1000 / credits_1500) to the
      // platform-specific product ID registered with Google Play / App Store.
      const creditIds = getCreditIdsForPlatform();
      const CREDIT_ID_MAP = {
        'credits_500': creditIds.CREDITS_500,
        'credits_1000': creditIds.CREDITS_1000,
        'credits_1500': creditIds.CREDITS_1500,
      };
      const nativeCreditId = CREDIT_ID_MAP[creditPackId] || creditPackId;
      console.log(`🛒 Native credit purchase: generic=${creditPackId} → native=${nativeCreditId} on ${platform}`);
      
      const storeProduct = store.get(nativeCreditId);
      if (!storeProduct) {
        await store.update();
      }
      
      const finalProduct = store.get(nativeCreditId);
      const offer = finalProduct?.getOffer?.();
      
      if (offer || finalProduct) {
        // Global approved handler (registered in initializeStore) handles verification.
        const orderOpts = { applicationUsername: userId };
        let orderResult = null;
        try {
          orderResult = offer ? await offer.order(orderOpts) : await store.order(finalProduct, orderOpts);
        } catch (e) {
          orderResult = e;
        }
        if (orderResult) {
          const isCancelled = orderResult.code === 6777006 || (orderResult.message || '').toLowerCase().includes('cancel');
          return { success: false, cancelled: isCancelled, message: isCancelled ? 'Purchase cancelled' : (orderResult.message || 'Purchase failed') };
        }
        
        // Listen for iap-result (credits path uses same event; consumer decides on type)
        return new Promise((resolve) => {
          const handler = (e) => { window.removeEventListener('iap-result', handler); resolve(e.detail); };
          window.addEventListener('iap-result', handler);
          setTimeout(() => { window.removeEventListener('iap-result', handler); resolve({ success: false, message: 'Purchase timed out' }); }, 90000);
        });
      }
      // Product not found in store — on Android/iOS DO NOT silently fall back
      // to Razorpay. Fail loudly so user knows to retry / contact support.
      return {
        success: false,
        message: `Product unavailable on ${platform === 'ios' ? 'App Store' : 'Google Play'}. Please retry in a moment.`,
      };
    }
    
    // WEB ONLY: Create order first (backend decides mock vs production)
    const isDemoDevice = localStorage.getItem('is_test_device') === 'true';
    const orderRes = await fetch(`${API_URL}/api/razorpay/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        package_id: creditPackId,
        user_id: userId,
        email: localStorage.getItem('user_email') || '',
        name: localStorage.getItem('user_name') || 'User',
        phone: localStorage.getItem('user_phone') || '',
        demo_mode: isDemoDevice
      })
    });
    const orderData = await orderRes.json();
    
    if (!orderData.success) {
      return { success: false, message: orderData.detail || 'Failed to create order' };
    }
    
    if (orderData.mock) {
      // ===== MOCK MODE: Auto-verify credits =====
      console.log('Mock credit refill — auto-verifying...');
      const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razorpay_order_id: orderData.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock',
          user_id: userId,
          demo_mode: isDemoDevice
        })
      });
      const verifyData = await verifyRes.json();
      
      if (verifyData.success) {
        const creditsAdded = verifyData.credits_added || orderData.package?.sage_credits || 0;
        // Additive: add to existing balance. Also fetch live balance from backend as source of truth.
        const currentLocal = parseInt(localStorage.getItem('sage_credits') || '0', 10);
        localStorage.setItem('sage_credits', String((Number.isFinite(currentLocal) ? currentLocal : 0) + creditsAdded));
        window.dispatchEvent(new Event('credits-updated'));
        // Reconcile with backend live balance in the background (overrides any drift)
        try {
          fetch(`${API_URL}/api/razorpay/subscription/${userId}`)
            .then(r => r.json())
            .then(d => {
              const liveCredits = d?.subscription?.credits;
              if (typeof liveCredits === 'number') {
                localStorage.setItem('sage_credits', String(liveCredits));
                window.dispatchEvent(new Event('credits-updated'));
              }
            })
            .catch(() => {});
        } catch {}
        
        return {
          success: true,
          mock: true,
          credits_added: creditsAdded,
          message: `+${creditsAdded} Sage Coins added!`
        };
      }
      return { success: false, message: verifyData.detail || 'Credit verification failed' };
    } else {
      // ===== PRODUCTION MODE: Payment Link redirect =====
      console.log('Production credit refill — redirecting to payment link...');
      return await redirectToPaymentLink(creditPackId, userId);
    }
  } catch (e) {
    console.warn('Credit refill payment failed:', e.message);
    const { sanitizeErrorMessage } = await import('../utils/safeJson');
    return { success: false, message: sanitizeErrorMessage(e.message, 'Payment failed. Please try again.') };
  }
};

export default {
  PRODUCT_IDS,
  PRODUCT_DETAILS,
  getPlatform,
  isNative,
  initializeStore,
  getProduct,
  purchaseProduct,
  purchaseWithRazorpay,
  purchaseCreditRefill,
  restorePurchases,
  checkSubscriptionStatus,
  isFeatureUnlocked,
  getUnlockedFeatures,
  getSageCredits,
  useSageCredits,
  generatePremiumContent,
};
